import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { OnboardingStatus, PendingActor, User } from '../types';
import { apiClient } from '../services/api';

interface UserContextType {
  user: User | null;
  pendingActor: PendingActor | null;
  isLoading: boolean;
  /** Onboarding/SBP status fetched once at app bootstrap (App.tsx). */
  onboardingStatus: OnboardingStatus | null;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  user: User | null;
  pendingActor: PendingActor | null;
  isLoading: boolean;
  onboardingStatus: OnboardingStatus | null;
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({
  user: initialUser,
  pendingActor,
  isLoading,
  onboardingStatus,
  children,
}) => {
  const [user, setUser] = useState<User | null>(initialUser);

  // Update local user when prop changes
  useEffect(() => {
    setUser(initialUser);
  }, [initialUser]);

  const refreshUser = useCallback(async () => {
    try {
      const freshUser = await apiClient.getUserProfile();
      setUser(freshUser);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;

    const id = window.setInterval(() => {
      void refreshUser();
    }, 60_000);

    return () => window.clearInterval(id);
  }, [userId, refreshUser]);

  return (
    <UserContext.Provider value={{ user, pendingActor, isLoading, onboardingStatus, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
